/**
 * WeatherChart — pure-SVG day curve: temperature line + rain bars + wind dots.
 *
 * Dependency-free, no canvas, no animation library. Renders into a host
 * element via innerHTML for perf; refreshes by re-rendering. Sized by the
 * caller (responsive: width 100% via CSS, height fixed in px).
 */

import type { WeatherDetails } from '../home/types';

interface ChartOptions {
  height?: number;
  /** Show hour labels under the X axis. */
  hourLabels?: boolean;
  /** Pick which series to show. Default: all three. */
  show?: { temp?: boolean; rain?: boolean; wind?: boolean };
  /** Color overrides — defaults pull from CSS vars at render time. */
  tempColor?: string;
  rainColor?: string;
  windColor?: string;
  /** A11y label */
  ariaLabel?: string;
}

const DEFAULT_HEIGHT = 160;
const PAD_TOP = 16;
const PAD_BOTTOM = 28;
const PAD_X = 16;
const VIEWBOX_W = 720; // arbitrary unit; CSS scales to container width

/** Render a 24h day curve into the given host. The SVG fills the host's
 *  width via viewBox; height is fixed in px. */
export function renderWeatherChart(
  host: HTMLElement,
  data: WeatherDetails,
  opts: ChartOptions = {},
): void {
  const height = opts.height ?? DEFAULT_HEIGHT;
  const show = { temp: true, rain: true, wind: true, ...opts.show };
  const tempColor = opts.tempColor ?? 'var(--ln-accent, #0A0A0A)';
  const rainColor = opts.rainColor ?? '#3B82F6';
  const windColor = opts.windColor ?? '#94A3B8';

  const hours = data.hourly.slice(0, 24);
  if (hours.length < 2) {
    host.innerHTML = '';
    return;
  }

  const temps = hours.map((h) => h.temp);
  const rains = hours.map((h) => h.rain);
  const winds = hours.map((h) => h.wind);

  const tMin = Math.min(...temps);
  const tMax = Math.max(...temps);
  const tSpan = Math.max(1, tMax - tMin);
  const rainMax = Math.max(0.5, ...rains);
  const windMax = Math.max(5, ...winds);

  const n = hours.length;
  const innerW = VIEWBOX_W - PAD_X * 2;
  const innerH = height - PAD_TOP - PAD_BOTTOM;
  const xAt = (i: number) => PAD_X + (i / (n - 1)) * innerW;
  const tempY = (v: number) => PAD_TOP + (1 - (v - tMin) / tSpan) * innerH;
  const rainBarH = (v: number) => Math.max(0, (v / rainMax) * (innerH * 0.45));
  const windY = (v: number) => PAD_TOP + (1 - v / windMax) * innerH;

  // Smooth Catmull-Rom-ish path for the temp curve
  const pathD = buildSmoothPath(temps.map((t, i) => [xAt(i), tempY(t)] as [number, number]));

  // Rain bars
  const bars = show.rain
    ? hours
        .map((h, i) => {
          const w = innerW / n / 1.6;
          const x = xAt(i) - w / 2;
          const bh = rainBarH(h.rain);
          const y = PAD_TOP + innerH - bh;
          return bh > 0
            ? `<rect x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${w.toFixed(1)}" height="${bh.toFixed(1)}" rx="2" fill="${rainColor}" opacity="0.55"/>`
            : '';
        })
        .join('')
    : '';

  // Wind dots
  const dots = show.wind
    ? hours
        .map((_, i) => {
          if (i % 3 !== 0) return '';
          const cx = xAt(i);
          const cy = windY(winds[i]);
          return `<circle cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" r="2.4" fill="${windColor}"/>`;
        })
        .join('')
    : '';

  // Temp endpoints + max/min labels
  const maxIdx = temps.indexOf(tMax);
  const minIdx = temps.indexOf(tMin);
  const labelMaxX = xAt(maxIdx);
  const labelMaxY = tempY(tMax) - 6;
  const labelMinX = xAt(minIdx);
  const labelMinY = tempY(tMin) + 14;

  // Hour labels (every 6h)
  const hourLabels = opts.hourLabels !== false
    ? hours
        .map((h, i) => {
          if (i % 6 !== 0 && i !== n - 1) return '';
          const date = new Date(h.time);
          const hh = isNaN(date.getTime())
            ? `${i}`
            : `${date.getHours().toString().padStart(2, '0')}h`;
          const x = xAt(i);
          const y = height - 8;
          return `<text x="${x.toFixed(1)}" y="${y}" text-anchor="middle" font-size="10" fill="var(--text-secondary, #64748B)">${hh}</text>`;
        })
        .join('')
    : '';

  // Soft area under temp curve for visual weight
  const areaD = `${pathD} L ${xAt(n - 1).toFixed(1)} ${(PAD_TOP + innerH).toFixed(1)} L ${PAD_X.toFixed(1)} ${(PAD_TOP + innerH).toFixed(1)} Z`;

  const aria = opts.ariaLabel ?? 'Weather forecast for next 24 hours';

  host.innerHTML = `
    <svg viewBox="0 0 ${VIEWBOX_W} ${height}" preserveAspectRatio="none"
         role="img" aria-label="${escAttr(aria)}"
         style="display:block;width:100%;height:${height}px;overflow:visible;">
      <defs>
        <linearGradient id="awTempGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stop-color="${tempColor}" stop-opacity="0.18"/>
          <stop offset="100%" stop-color="${tempColor}" stop-opacity="0"/>
        </linearGradient>
      </defs>
      ${bars}
      ${show.temp ? `<path d="${areaD}" fill="url(#awTempGrad)"/>` : ''}
      ${show.temp ? `<path d="${pathD}" fill="none" stroke="${tempColor}" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"/>` : ''}
      ${dots}
      ${show.temp ? `
        <text x="${labelMaxX.toFixed(1)}" y="${labelMaxY.toFixed(1)}" text-anchor="middle" font-size="11" font-weight="700" fill="${tempColor}">${Math.round(tMax)}°</text>
        <text x="${labelMinX.toFixed(1)}" y="${labelMinY.toFixed(1)}" text-anchor="middle" font-size="11" font-weight="600" fill="var(--text-secondary, #64748B)">${Math.round(tMin)}°</text>
      ` : ''}
      ${hourLabels}
    </svg>
  `;
}

/**
 * Tide curve — sea level vs time over the next ~48h. Marks the next
 * `markCount` extrema with a small label and a vertical guide. Uses the
 * same SVG idiom as renderWeatherChart so styling stays consistent.
 */
export function renderTideChart(
  host: HTMLElement,
  series: Array<{ time: string; height: number }>,
  tides: Array<{ time: string; height: number; type: 'high' | 'low' }>,
  opts: { height?: number; markCount?: number; lineColor?: string } = {},
): void {
  const data = series.slice(0, 48);
  if (data.length < 2) {
    host.innerHTML = '';
    return;
  }
  const height = opts.height ?? 140;
  const lineColor = opts.lineColor ?? '#0EA5E9';
  const innerW = VIEWBOX_W - PAD_X * 2;
  const innerH = height - PAD_TOP - PAD_BOTTOM;
  const heights = data.map((d) => d.height);
  const min = Math.min(...heights);
  const max = Math.max(...heights);
  const span = Math.max(0.1, max - min);
  const xAt = (i: number) => PAD_X + (i / (data.length - 1)) * innerW;
  const yAt = (h: number) => PAD_TOP + (1 - (h - min) / span) * innerH;
  const pts = data.map((d, i) => [xAt(i), yAt(d.height)] as [number, number]);
  const lineD = buildSmoothPath(pts);
  const areaD = `${lineD} L ${xAt(data.length - 1).toFixed(1)} ${(PAD_TOP + innerH).toFixed(1)} L ${PAD_X.toFixed(1)} ${(PAD_TOP + innerH).toFixed(1)} Z`;

  // Tide markers — only events that fall inside the visible series.
  const lastT = data[data.length - 1].time;
  const visibleTides = tides
    .filter((t) => t.time <= lastT)
    .slice(0, opts.markCount ?? 4);
  const markers = visibleTides
    .map((tEv) => {
      const idx = data.findIndex((d) => d.time === tEv.time);
      if (idx < 0) return '';
      const x = xAt(idx);
      const y = yAt(tEv.height);
      const date = new Date(tEv.time);
      const hh = isNaN(date.getTime())
        ? ''
        : date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
      const isHigh = tEv.type === 'high';
      const dotColor = isHigh ? '#0EA5E9' : '#94A3B8';
      const labelY = isHigh ? Math.max(8, y - 8) : Math.min(height - 4, y + 14);
      const label = `${hh} · ${tEv.height.toFixed(2)}m`;
      return `
        <line x1="${x.toFixed(1)}" x2="${x.toFixed(1)}" y1="${PAD_TOP}" y2="${(PAD_TOP + innerH).toFixed(1)}"
              stroke="var(--border, #E5E7EB)" stroke-dasharray="2 3" stroke-width="1"/>
        <circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="3.6" fill="${dotColor}"/>
        <text x="${x.toFixed(1)}" y="${labelY.toFixed(1)}" text-anchor="middle"
              font-size="10" font-weight="600" fill="var(--text-primary, #0A0A0A)">${escAttr(label)}</text>
      `;
    })
    .join('');

  // Minimal hour labels (every ~12h)
  const hourLabels = data
    .map((d, i) => {
      if (i === 0 || i === data.length - 1 || i % 12 !== 0) return '';
      const date = new Date(d.time);
      const hh = isNaN(date.getTime()) ? '' : `${date.getHours().toString().padStart(2, '0')}h`;
      return `<text x="${xAt(i).toFixed(1)}" y="${height - 8}" text-anchor="middle"
                    font-size="10" fill="var(--text-secondary, #64748B)">${hh}</text>`;
    })
    .join('');

  host.innerHTML = `
    <svg viewBox="0 0 ${VIEWBOX_W} ${height}" preserveAspectRatio="none"
         role="img" aria-label="Tide forecast"
         style="display:block;width:100%;height:${height}px;overflow:visible;">
      <defs>
        <linearGradient id="awTideGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stop-color="${lineColor}" stop-opacity="0.22"/>
          <stop offset="100%" stop-color="${lineColor}" stop-opacity="0"/>
        </linearGradient>
      </defs>
      <path d="${areaD}" fill="url(#awTideGrad)"/>
      <path d="${lineD}" fill="none" stroke="${lineColor}" stroke-width="2.2"
            stroke-linecap="round" stroke-linejoin="round"/>
      ${markers}
      ${hourLabels}
    </svg>
  `;
}

/** Tiny sparkline for the home widget. ~88×24 visual, viewBox-scaled. */
export function renderTempSparkline(
  host: HTMLElement,
  hourlyTemp: number[],
  color = 'var(--ln-accent, #0A0A0A)',
): void {
  if (hourlyTemp.length < 2) {
    host.innerHTML = '';
    return;
  }
  const w = 88, h = 24, pad = 2;
  const min = Math.min(...hourlyTemp);
  const max = Math.max(...hourlyTemp);
  const span = Math.max(1, max - min);
  const n = hourlyTemp.length;
  const xAt = (i: number) => pad + (i / (n - 1)) * (w - pad * 2);
  const yAt = (v: number) => pad + (1 - (v - min) / span) * (h - pad * 2);
  const d = buildSmoothPath(hourlyTemp.map((v, i) => [xAt(i), yAt(v)] as [number, number]));
  host.innerHTML = `
    <svg viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" aria-hidden="true"
         style="display:block;overflow:visible;">
      <path d="${d}" fill="none" stroke="${color}" stroke-width="1.5"
            stroke-linecap="round" stroke-linejoin="round" opacity="0.85"/>
    </svg>
  `;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Catmull-Rom-to-Bezier smoothing. Tension 0.5. */
function buildSmoothPath(pts: Array<[number, number]>): string {
  if (pts.length < 2) return '';
  const t = 0.5;
  const seg: string[] = [`M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`];
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] ?? pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] ?? p2;
    const cp1x = p1[0] + ((p2[0] - p0[0]) / 6) * t * 2;
    const cp1y = p1[1] + ((p2[1] - p0[1]) / 6) * t * 2;
    const cp2x = p2[0] - ((p3[0] - p1[0]) / 6) * t * 2;
    const cp2y = p2[1] - ((p3[1] - p1[1]) / 6) * t * 2;
    seg.push(`C ${cp1x.toFixed(1)} ${cp1y.toFixed(1)}, ${cp2x.toFixed(1)} ${cp2y.toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`);
  }
  return seg.join(' ');
}

function escAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}
